import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { DatabaseIcon, RocketIcon, HardDriveIcon, RefreshCwIcon } from 'lucide-react'
import { MetricChart } from './MetricChart'
import { csrfHeaders } from '@/lib/csrf'
import { formatDuration } from './utils'
import type { MetricData, DeploymentHealth } from './types'

interface ServiceHealth {
  status: string
  metrics: Record<string, MetricData | null>
}

interface PlatformHealth {
  database: ServiceHealth
  cache: ServiceHealth
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('projects')

  const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; className: string }> = {
    healthy: { variant: 'secondary', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800' },
    degraded: { variant: 'secondary', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800' },
    unhealthy: { variant: 'destructive', className: '' },
    unknown: { variant: 'outline', className: '' }
  }

  const config = variants[status] || variants.unknown
  const label = t(`production.platform.${status}`)

  return <Badge variant={config.variant} className={config.className}>{label}</Badge>
}

function formatBytes(bytes: number): string {
  if (bytes >= 1e12) return `${(bytes / 1e12).toFixed(1)} TB`
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`
  if (bytes >= 1e3) return `${(bytes / 1e3).toFixed(1)} KB`
  return `${bytes} B`
}

function formatLatency(seconds: number): string {
  if (seconds < 0.001) return `${(seconds * 1000000).toFixed(0)}us`
  if (seconds < 1) return `${(seconds * 1000).toFixed(1)}ms`
  return `${seconds.toFixed(2)}s`
}

function MetricRow({ label, value, unit }: { label: string; value: number | null | undefined; unit?: string }) {
  if (value == null) return null

  let formatted: string
  switch (unit) {
    case 'Bytes': formatted = formatBytes(value); break
    case 'Seconds': formatted = formatLatency(value); break
    case 'Percent': formatted = `${value.toFixed(1)}%`; break
    case 'Duration': formatted = formatDuration(Math.round(value)); break
    default: formatted = value.toFixed(0)
  }

  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">{formatted}</span>
    </div>
  )
}

function HealthCard({
  title,
  icon: Icon,
  service,
  metricConfig
}: {
  title: string
  icon: React.ElementType
  service: ServiceHealth | null
  metricConfig: { key: string; label: string; chartColor?: string }[]
}) {
  if (!service) {
    return (
      <Card className="shadow-none">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">{title}</CardTitle>
            </div>
            <Skeleton className="h-5 w-16" />
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-[60px] w-full" />
          <Skeleton className="h-4 w-full" />
        </CardContent>
      </Card>
    )
  }

  // Split metrics: first 2 get charts, rest are text rows
  const chartMetrics = metricConfig.slice(0, 2)
  const textMetrics = metricConfig.slice(2)

  return (
    <Card className="shadow-none">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">{title}</CardTitle>
          </div>
          <StatusBadge status={service.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Top metrics with inline values */}
        <div className="flex gap-4">
          {chartMetrics.map(({ key, label }) => {
            const metric = service.metrics[key]
            return (
              <MetricRow
                key={key}
                label={label}
                value={metric?.current}
                unit={metric?.unit}
              />
            )
          })}
        </div>

        {/* Mini charts */}
        <div className="grid grid-cols-2 gap-2">
          {chartMetrics.map(({ key, label, chartColor }) => {
            const metric = service.metrics[key]
            const chartUnit = metric?.unit === 'Percent' ? '%' : metric?.unit === 'Seconds' ? 'ms' : undefined
            return (
              <MetricChart
                key={key}
                datapoints={metric?.datapoints}
                color={chartColor || 'hsl(var(--primary))'}
                height={50}
                label={label}
                unit={chartUnit}
              />
            )
          })}
        </div>

        {/* Bottom text metrics */}
        <div className="space-y-1">
          {textMetrics.map(({ key, label }) => {
            const metric = service.metrics[key]
            return (
              <MetricRow
                key={key}
                label={label}
                value={metric?.current}
                unit={metric?.unit}
              />
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

function DeploymentHealthCard({ health }: { health: DeploymentHealth }) {
  const { t } = useTranslation('projects')

  const status = health.success_rate >= 90 ? 'healthy'
    : health.success_rate >= 70 ? 'degraded'
    : 'unhealthy'

  return (
    <Card className="shadow-none">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RocketIcon className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">{t('production.platform.deployments')}</CardTitle>
          </div>
          <StatusBadge status={status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-4">
          <MetricRow label={t('production.platform.successRate')} value={health.success_rate} unit="Percent" />
          <MetricRow label={t('production.platform.avgDuration')} value={health.avg_duration_seconds} unit="Duration" />
        </div>

        <MetricChart
          datapoints={health.daily}
          color="hsl(150, 60%, 45%)"
          height={50}
          label={t('production.platform.deploys')}
        />

        <div className="space-y-1">
          <MetricRow label={t('production.platform.total30d')} value={health.total} />
          <MetricRow label={t('production.platform.failed30d')} value={health.failed} />
        </div>
      </CardContent>
    </Card>
  )
}

export function PlatformHealthSection({ deploymentHealth }: { deploymentHealth: DeploymentHealth }) {
  const { t } = useTranslation('projects')
  const [health, setHealth] = useState<PlatformHealth | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const fetchedRef = useRef(false)

  const fetchHealth = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await fetch('/api/production/platform_health', { headers: csrfHeaders() })
      if (!res.ok) throw new Error('Failed')
      const data = await res.json()
      setHealth(data)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (fetchedRef.current) return
    fetchedRef.current = true
    fetchHealth()
  }, [fetchHealth])

  if (error && !health) {
    return (
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-4">{t('production.platform.title')}</h2>
        <Card className="shadow-none">
          <CardContent className="py-8 text-center">
            <p className="text-sm text-muted-foreground mb-2">{t('production.platform.loadFailed')}</p>
            <Button variant="outline" size="sm" onClick={fetchHealth}>
              <RefreshCwIcon className="h-3 w-3 mr-1" />
              {t('production.platform.retry')}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="mb-6">
      <h2 className="text-lg font-semibold mb-4">{t('production.platform.title')}</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <HealthCard
          title={t('production.platform.database')}
          icon={DatabaseIcon}
          service={loading ? null : health?.database ?? null}
          metricConfig={[
            { key: 'cpu', label: t('production.platform.cpu'), chartColor: 'hsl(220, 70%, 55%)' },
            { key: 'connections', label: t('production.platform.connections'), chartColor: 'hsl(260, 60%, 55%)' },
            { key: 'free_storage', label: t('production.platform.storage') },
            { key: 'write_latency', label: t('production.platform.writeLatency') }
          ]}
        />
        <DeploymentHealthCard health={deploymentHealth} />
        <HealthCard
          title={t('production.platform.cache')}
          icon={HardDriveIcon}
          service={loading ? null : health?.cache ?? null}
          metricConfig={[
            { key: 'hit_rate', label: t('production.platform.hitRate'), chartColor: 'hsl(30, 80%, 55%)' },
            { key: 'cpu', label: t('production.platform.cpu'), chartColor: 'hsl(50, 80%, 50%)' },
            { key: 'evictions', label: t('production.platform.evictions') },
            { key: 'memory', label: t('production.platform.memory') }
          ]}
        />
      </div>
    </div>
  )
}
